/* eslint-disable import/no-unresolved */
import {
  applyFilterWithManager,
  fallback,
  FilterManager,
  KlevuConfig,
  KlevuFetch,
  KlevuKMCSettings,
  listFilters,
  search,
  sendSearchEvent,
  trendingProducts,
} from 'https://cdn.skypack.dev/pin/@klevu/core@v1.1.5-0q1n6dBIy9DgOZifPmpX/mode=imports,min/optimized/@klevu/core.js'
/* eslint-enable import/no-unresolved */

KlevuConfig.init(window.BAO.KlevuConfig)

const { debounce } = window.BAO.Utils

class SearchResults extends window.BAO.CustomElement() {
  static get requiredElements () {
    return ['category']
  }

  async connectedCallback () {
    super.connectedCallback()

    this.search = debounce(this.search.bind(this), 100)

    this.minimumSearchTermLength = 3
    this.data = {
      queryResults: [],
    }

    this.redirects = await this.getRedirects()

    const initialValue = this.getAttribute('value')
    if (initialValue) {
      this.search(initialValue)
    }
  }

  setupListeners () {
    super.setupListeners()

    if (this.form) {
      this.listeners.add(this.form, 'input', event =>
        this.handleFormInput(event),
      )

      this.listeners.add(this.form, 'submit', event =>
        this.handleFormSubmit(event),
      )
    }

    if (this.searchInput) {
      this.listeners.add(this.searchInput, 'blur', event =>
        this.handleInputBlur(event),
      )
    }

    this.listeners.add(this, 'search-results:next', event =>
      this.loadNextPage(event),
    )
    this.listeners.add(this, 'search-results:refresh', () =>
      this.search(this.getAttribute('value')),
    )
  }

  /** @param {InputEvent} event */
  handleFormInput (event) {
    const term = event.target.value

    if (term.length === 0) {
      this.state = 'no-search-term'
    }

    if (term.length < this.minimumSearchTermLength) {
      return
    }

    this.search(term)
  }

  /** @param {SubmitEvent} event */
  handleFormSubmit (event) {
    const term = this.searchInput.value

    if (term === '') {
      return
    }

    this.saveRecentSearch(term)

    const matchedRedirect = this.redirects.find(redirect => {
      return redirect.keywords.includes(term.toLowerCase())
    })

    if (!matchedRedirect) {
      return
    }

    event.preventDefault()
    window.location.href = matchedRedirect.url
  }

  /** @param {FocusEvent} event */
  handleInputBlur (event) {
    if (event.target.value === '') {
      return
    }

    this.saveRecentSearch(event.target.value)
  }

  /** @param {string} search */
  saveRecentSearch (search) {
    let existingSearches = []

    try {
      const storedSearches = JSON.parse(
        window.localStorage.getItem('search-results:recent-searches'),
      )
      if (Array.isArray(storedSearches)) {
        existingSearches = storedSearches
      }
    } catch (error) {
      // If there's an error, keep existingSearches as an empty array
    }

    /**
     * @note Set is used here to remove duplicates. We still add
     * the new term to the front so if it already existed in the
     * array, it will be pushed to the top
     */
    existingSearches = new Set([search, ...existingSearches])

    window.localStorage.setItem(
      'search-results:recent-searches',
      JSON.stringify([...existingSearches].slice(0, 5)),
    )
  }

  /** @param {CustomEvent<{ queryId: string; }>} event */
  async loadNextPage (event) {
    const { queryId } = event.detail
    const associatedQuery = this.data.queryResults.find(
      query => query.id === queryId,
    )
    const associatedQueryIndex = this.data.queryResults.findIndex(
      query => query.id === queryId,
    )

    if (!associatedQuery) {
      return
    }

    this.isBusy = true
    try {
      const results = await associatedQuery.next()
      const existingRecords = [...associatedQuery.records]
      const newRecords = results.queriesById(queryId).records

      this.data.queryResults[associatedQueryIndex] = results.queriesById(
        queryId,
      )
      this.data.queryResults[associatedQueryIndex].records = [
        ...existingRecords,
        ...newRecords,
      ]

      await Promise.all(
        this.els.category.elements.map(category => {
          return category.render(this.data.queryResults[associatedQueryIndex])
        }),
      )
    } finally {
      this.isBusy = false
    }
  }

  /** @param {string} term */
  async search (term) {
    this.isBusy = true

    const categoryEls = this.els.category.elements.filter(
      category => category.query,
    )

    try {
      const results = await KlevuFetch(
        ...categoryEls.map(category => {
          const modifiers = [...category.modifiers]

          /**
           * @note We attach searchSearchEvent here to avoid paginated
           * searches sending duplicate analytics events
           */
          if (category.query.typeOfRecords.includes('KLEVU_PRODUCT')) {
            modifiers.push(sendSearchEvent())
          }

          return search(term, category.query, ...modifiers)
        }),
      )

      const queryIds = results.apiResponse.queryResults.map(query => query.id)

      this.data.queryResults = queryIds.map(id => results.queriesById(id))

      const hasResults = this.data.queryResults.some(
        query => query.records.length > 0,
      )
      this.state = hasResults ? 'with-results' : 'no-results'

      await Promise.all(
        categoryEls.map(category => {
          const hasFallbackQuery = category.hasAttribute('with-fallback')

          const query = results.queriesById(category.queryId)
          let fallbackQuery
          if (hasFallbackQuery && query.records.length === 0) {
            fallbackQuery = results.queriesById(`${category.queryId}-fallback`)
          }

          return category.render(query, fallbackQuery)
        }),
      )

      if (this.els['no-results']) {
        await this.els['no-results'].element.render({ query: term })
      }
    } finally {
      this.isBusy = false
    }
  }

  async getUserSettings () {
    return KlevuKMCSettings()
  }

  async getRedirects () {
    const userSettings = await this.getUserSettings()

    return userSettings.maps.klevu_keywordUrlMap || []
  }

  /** @returns {HTMLFormElement} */
  get form () {
    return this.externalEls.form.elements.find(
      form => form.dataset.for === this.id,
    )
  }

  /** @returns {HTMLInputElement} */
  get searchInput () {
    return this.externalEls['search-input'].elements.find(
      input => input.dataset.for === this.id,
    )
  }

  get isBusy () {
    return this.getAttribute('aria-busy') === 'true'
  }

  set isBusy (isBusy) {
    return this.setAttribute('aria-busy', isBusy)
  }

  /** @returns {'no-search-term'|'no-results'|'with-results'} */
  get state () {
    return this.getAttribute('state')
  }

  set state (state) {
    this.setAttribute('state', state)
  }
}

if (!customElements.get('search-results')) {
  customElements.define('search-results', SearchResults)
}

class SearchResultsQuery extends window.BAO.CustomElement() {
  constructor () {
    super()

    if (this.hasAttribute('with-filters')) {
      this.filterManager = new FilterManager()
    }
  }

  /**
   * @param {Record<string, unknown>} query
   *
   * @note Overwrite this method with your own to display search results
   */
  render (query) {}

  get limit () {
    if (!this.hasAttribute('limit')) {
      return
    }

    return Number(this.getAttribute('limit'))
  }

  /** @returns {string} */
  get sortMethod () {
    if (!this.hasAttribute('sort')) {
      return
    }

    return this.getAttribute('sort')
  }

  /** @returns {string} */
  get queryId () {
    return this.getAttribute('query-id')
  }

  get query () {
    const baseQuery = {
      id: this.queryId,
      typeOfRecords: this.typeOfRecords,
    }

    if (this.limit !== undefined) {
      baseQuery.limit = this.limit
    }

    if (this.sortMethod !== undefined) {
      baseQuery.sort = this.sortMethod
    }

    return baseQuery
  }

  get modifiers () {
    const modifiers = []

    if (this.hasAttribute('with-filters')) {
      modifiers.push(
        applyFilterWithManager(this.filterManager),
        listFilters({
          filterManager: this.filterManager,
          limit: 250,
          rangeFilterSettings: [
            {
              key: 'klevu_price',
              minMax: true,
            },
          ],
        }),
      )
    }

    if (this.hasAttribute('with-fallback')) {
      modifiers.push(fallback(trendingProducts({ limit: this.limit })))
    }

    return modifiers
  }

  get typeOfRecords () {
    const typeOfRecords = {
      collections: 'KLEVU_CATEGORY',
      cms: 'KLEVU_CMS',
      products: 'KLEVU_PRODUCT',
    }

    /** @type {keyof typeOfRecords} */
    const categories = this.getAttribute('category').split(' ')

    return categories.map(category => typeOfRecords[category])
  }
}

window.BAO.Search = window.BAO.Search || {}
window.BAO.Search.components = {
  SearchResultsQuery,
}
